// Angular
import { Inject, Injectable, OnDestroy } from '@angular/core';

// Third party
import Pusher from 'pusher-js/with-encryption';
import * as _ from 'lodash';

// Local Library
import {
  IBalancePusher,
  IGetEventsDetailsResponse,
  IPusher,
  LoggerService,
  TError,
  TO_DECRYPT,
  Web
} from '@amwager/util-ts';

// Local file
import { PUSHER } from '../tokens';
import { CommonFacade } from '../+state';
import { SessionService } from './session.service';


/**
 * Real-time updates over Pusher — subscribes, receives, routes commands
 */
@Injectable()
export class PusherService implements OnDestroy {

  private selectedEventTrack!: IGetEventsDetailsResponse;

  private pusher: any;
  private isPusherConnected = false;
  private pusherKeyDecrypted: Promise<string>;
  private updateGeneralChannel: any;
  private updateOddsChannel: any;
  private updateCustomerChannel: any;

  private runId!: string;
  private selectedRace!: any;
  private subscription?: string;

  constructor(
    @Inject(PUSHER) private readonly pusherEnv: any,
    private readonly commonFacade: CommonFacade,
    private readonly sessionService: SessionService,
    private readonly loggerService: LoggerService,
  ) {

    this.pusherKeyDecrypted = TO_DECRYPT(this.pusherEnv?.key);
  }

  /**
   * Disconnect Pusher on destroy
   * @returns {void}
   */
  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Connect to Pusher and set up error/subscription handlers
   * @returns {Promise<void>}
   */
  async connect(): Promise<void> {
    if (this.isPusherConnected) return;

    this.disconnect();
    this.pusher = undefined;

    this.commonFacade.log('Pusher: connecting...');

    try {
      this.pusher = new Pusher(
        await this.pusherKeyDecrypted,
        {
          cluster: this.pusherEnv?.cluster,
        }
      );
      this.loggerService.log('Pusher: connected');
      this.commonFacade.log('Pusher: connected');
      this.isPusherConnected = true;


      this.pusher?.connection.unbind("error");
      this.pusher?.connection.bind("error", (error: TError) => {
        this.loggerService.error(`Pusher: ${error}`);
        this.commonFacade.log(`ERROR Pusher: ${error}`);
        this.isPusherConnected = false;
      });

      this.reconnections();
      this.subscriptions();

      // Re-subscribe odds if a track was selected before Pusher connected
      if (this.selectedEventTrack) this.setSelectedEventTrack(this.selectedEventTrack);
    }
    catch (error) {
      this.loggerService.error(`Pusher: not connecting: ${error}`);
      this.commonFacade.log(`ERROR Pusher: ${error}`);

      this.pusher = undefined;
      this.isPusherConnected = false;
    }
  }

  /**
   * Disconnect from Pusher
   * @returns {void}
   */
  disconnect(): void {
    this.commonFacade.log('Pusher: disconnect');
    this.loggerService.log('Pusher: disconnect');
    this.pusher?.disconnect();
    this.isPusherConnected = false;
  }

  /**
   * Bind reconnection handlers for the Pusher connection
   * @returns {void}
   */
  private reconnections(): void {
    // unavailable
    this.pusher?.connection.unbind("unavailable");
    this.pusher.connection.bind('unavailable', () => {
      this.commonFacade.log('Pusher is unavailable. Reconnecting...');
      this.connect();
    });

    // disconnected
    this.pusher?.connection.unbind("disconnected");
    this.pusher.connection.bind('disconnected', () => {
      this.commonFacade.log('Pusher is disconnected. Reconnecting...');
      this.connect();
    });
  }

  /**
   * Subscribe to global and customer-specific Pusher channels
   * @returns {void}
   */
  private subscriptions(): void {
    const text = 'Pusher: setting subscriptions...';
    this.commonFacade.log(text);

    // general
    if (this.updateGeneralChannel) { // clean first
      try {
        this.pusher.unsubscribe("global");
      }
      catch (error) {
        this.commonFacade.log(`Pusher unsubscribe global: ${error}`);
      }

      this.updateGeneralChannel = undefined;
    }

    this.updateGeneralChannel = this.pusher.subscribe("global");
    this.updateGeneralChannel.unbind('ALL');
    this.updateGeneralChannel.bind('ALL', (data: any) => {
      this.processMessage(data);
    });

    // customer
    this.unSubscribeCustomer();
    this.subscribeCustomer();
  }

  /**
   * Unsubscribe from the customer-specific Pusher channel
   * @returns {void}
   */
  unSubscribeCustomer(): void {
    if (!this.subscription) return;

    const text = 'Pusher: Unsubscribing customer...';
    this.commonFacade.log(text);
    this.loggerService.log('PusherService: unSubscribeCustomer');

    try {
      this.pusher.unsubscribe('sub' + this.subscription);

      this.updateCustomerChannel = undefined;
      this.subscription = undefined;
    }
    catch (error) {
      this.commonFacade.log(`Pusher unsubscribe customer: ${error}`);
    }
  }

  /**
   * Subscribe to the customer-specific Pusher channel
   * @returns {void}
   */
  subscribeCustomer(): void {
    if (!this.sessionService.getSubscription()) return;

    const text = 'Pusher: Subscribing customer...';
    this.commonFacade.log(text);
    this.loggerService.log('PusherService: subscribeCustomer');

    this.subscription = this.sessionService.getSubscription();

    this.updateCustomerChannel = this.pusher.subscribe('sub' + this.subscription);
    this.updateCustomerChannel.unbind('ALL');
    this.updateCustomerChannel.bind('ALL', (data: any) => {
      this.processMessage(data);
    });
  }

  /**
   * Parse an incoming Pusher payload and dispatch to the command handler
   * @param {any} message Raw Pusher message
   * @returns {void}
   */
  private processMessage(message: any): void {
    if (!message) return;

    let msgJSON: any;
    let msgTemp = '';

    msgTemp = message.replace("\u0001", "");

    const firstMssgeIndex = msgTemp.indexOf("{");
    const lastMssgeIndex = msgTemp.lastIndexOf("}") + 1;

    msgTemp = msgTemp.substring(firstMssgeIndex, lastMssgeIndex);

    if (msgTemp.length > 1) {
      try {
        msgJSON = JSON.parse(msgTemp);
      }
      catch (error) {
        this.commonFacade.log(`Pusher: JSON format incorrect: ${msgTemp}`);
        return;
      }

      const command: string =  msgJSON.Command;
      this.commonFacade.setPusherData(command, msgJSON, 'PusherService:processMessage');
    }
  }

  /**
   * Route a Pusher command to its handler method
   * @param {string} command Pusher command code
   * @param {IPusher} data Parsed Pusher payload
   * @param {any} service Facade bag used by handlers
   * @returns {void}
   */
  commandController(command: string, data: IPusher, service: any): void {
    switch (command) {
        case "RB": this.RB(data); break;
        case "RCN": this.RCN(data, service?.wagerFacade); break;
        case "RCL": this.RCL(data, service?.wagerFacade); break;
        case "TD": this.TD(data); break;
        case "TE": this.TE(data); break;
        case "ES": this.ES(data); break;
        case "FI": this.FI(data, service?.wagerFacade); break;
        case "TI": this.TI(data); break;
        case "RO": this.RO(data, service?.wagerFacade); break;
        case "PO": this.PO(data, service?.wagerFacade); break;
        case "TS": this.TS(data); break;
        case "TC": this.TC(data); break;
        case "TO": this.TO(data); break;
        case "UC": this.UC(data); break;
        case "RUO": this.RUO(data); break;
        case "PM": this.PM(data, service?.wagerFacade); break;
        case "TTL": this.TTL(data); break;
        case "URC": this.URC(data, service?.wagerFacade); break;
        case "STI": this.STI(data, service?.accountFacade, service?.wagerFacade); break;
        case "STC": this.STC(data); break;
        case "SST": this.SST(data, service?.wagerFacade); break;
        case "STS": this.STS(data, service?.accountFacade, service?.wagerFacade); break;
        case "TIC": this.TIC(data, service?.accountFacade, service?.wagerFacade); break;
        case "STR": this.STR(data, service?.wagerFacade); break;
        case "FUS": this.FUS(data); break;
        case "FUC": this.FUC(data); break;
        case "TP": this.TP(data, service?.wagerFacade, service?.accountFacade, service?.myBets); break;
        case "BAL": this.BAL(data as unknown as IBalancePusher, service?.accountFacade); break;
        case "ROL": this.ROL(data, service?.sessionService); break;
        case "EXC": this.EXC(data); break;
        case "IDCHECK": this.IDCHECK(data); break;
        case "DOCCHECK": this.DOCCHECK(data); break;
        case "LGN": this.LGN(data, service?.sessionService); break;
        default: //do something
      }
  }


  /* eslint-disable  @typescript-eslint/no-unused-vars */
  /* eslint-disable  @typescript-eslint/no-empty-function */
  /**
   * RB — race begin
   * @param {IPusher} data Pusher payload
   * @returns {void}
   */
  private RB(data: IPusher): void {
    const value = data?.Data;
  }

  /**
   * RCN — race cancelled; refreshes race status
   * @param {IPusher} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private RCN(data: IPusher, wagerFacade?: object): void {
    this.loggerService.log('PusherService:RCN:data', data);

    try {
      (wagerFacade as any).updateRaceCancelled(
        'PusherService:RCN',
        data
      );
    } catch (error) {
      this.loggerService.error('Pusher: RCN: wagerFacade.updateRaceCancelled')
    }
  }

  /**
   * RCL — race closed; refreshes race status
   * @param {IPusher} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private RCL(data: IPusher, wagerFacade?: object): void {
    this.loggerService.log('PusherService:RCL:data', data);

    try {
      (wagerFacade as any).updateRaceClosed(
        'PusherService:RCL',
        data
      );
    } catch (error) {
      this.loggerService.error('Pusher: RCL: wagerFacade.updateRaceClosed')
    }
  }

  /**
   * FI — race final; refreshes race status and reloads bets
   * @param {IPusher} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private FI(data: IPusher, wagerFacade?: object): void {
    this.loggerService.log('PusherService:FI:data', data);

    try {
      (wagerFacade as any).updateRaceFinal(
        'PusherService:FI',
        data
      );
    } catch (error) {
      this.loggerService.error('Pusher: FI: wagerFacade.updateRaceFinal')
    }

    // update bets info
    try {
      const statuses = {
        3: 'active',
        6: 'close'
      };

      _.map([3, 6], (status) => {
        (wagerFacade as any).getBetsAPI(
          {
            runId: this.runId,
            ticketStatus: status,
            offset: 0,
            next: 500
          },
          (statuses as any)[status],
          'PusherService:FI'
        );
      });
    } catch (error) {
      this.loggerService.error('Pusher: FI: wagerFacade.getBetsAPI')
    }
  }

  /**
   * RO — race open; refreshes race status
   * @param {IPusher} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private RO(data: IPusher, wagerFacade?: object): void {
    this.loggerService.log('PusherService:RO:data', data);

    try {
      (wagerFacade as any).updateRaceOpen(
        'PusherService:RO',
        data
      );
    } catch (error) {
      this.loggerService.error('Pusher: RO: wagerFacade.updateRaceOpen')
    }
  }

  /**
   * PO — race post; refreshes race status
   * @param {IPusher} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private PO(data: IPusher, wagerFacade?: object): void {
    this.loggerService.log('PusherService:PO:data', data);

    try {
      (wagerFacade as any).updateRacePost(
        'PusherService:PO',
        data
      );
    } catch (error) {
      this.loggerService.error('Pusher: PO: wagerFacade.updateRacePost')
    }
  }

  /**
   * UC — race uncancelled
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private UC(data: any): void {
  }

  /**
   * RUO — race un-official
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private RUO(data: any): void {
  }

  /**
   * URC — race update; refreshes or fetches race data
   * @param {IPusher} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private URC(data: IPusher, wagerFacade?: object): void {
    this.loggerService.log('PusherService:URC:data', data);

    if (data?.Data) {
      try {
        (wagerFacade as any).updateRace(
          'PusherService:URC',
          data?.Data
        );
      }
      catch (error) {
        this.loggerService.error('Pusher: URC: wagerFacade.updateRace')
      }
    }
    else {
      try {
        (wagerFacade as any).getRace(
          'PusherService:URC',
          data?.RaceId,
          data?.RunId,
          data?.EventId
        );
      }
      catch (error) {
        this.loggerService.error('Pusher: URC: wagerFacade.getRace')
      }
    }

  }


  /**
   * TD — tote deactivate
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private TD(data: any): void {
  }

  /**
   * TE — tote end
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private TE(data: any): void {
  }

  /**
   * TI — tote initialize
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private TI(data: any): void {
  }

  /**
   * TS — tote stop
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private TS(data: any): void {
  }

  /**
   * TC — tote close
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private TC(data: any): void {
  }

  /**
   * TO — tote open; reloads the page
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private TO(data: any): void {
    window.location.reload();
  }


  /**
   * ES — event status update
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private ES(data: any): void {

  }

  /**
   * PM — pool matrix update; refreshes matrix/totals/odds
   * @param {any} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private PM(data: any, wagerFacade?: object): void {
    this.loggerService.log('PusherService:PM:', data);

    if (
      !(data?.RunId === this.selectedRace?.runId
        && data?.EventId === this.selectedRace?.eventId
        && data?.RaceId === this.selectedRace?.RaceId)
    ) return;

    try {
      if (data?.Data) {
        (wagerFacade as any).updateRaceMatrix(
          'PusherService:PM',
          data?.Data
        );
      }
      else {
        (wagerFacade as any).getPoolMatrix(
          'PusherService:PM',
          {
            raceId: data?.RaceId,
            eventId: data?.EventId,
            runId: data?.RunId,
            poolId: data?.PoolId
          },
          true
        );
      }

      // get pools totals
      (wagerFacade as any).getPoolsTotals(
        'PusherService:PM',
        {
          raceId: data?.RaceId,
          eventId: data?.EventId,
          runId: data?.RunId,
        },
        true
      );


      if (['WIN', 'TRU'].includes(data?.PoolId)) {
        // get race odd matrixes with WIN pool
        (wagerFacade as any).getRaceAllOddsMatrixes(
          'PusherService:PM',
          data?.PoolId,
          {
            raceId: data?.RaceId,
            eventId: data?.EventId,
            runId: data?.RunId,
            poolId: data?.PoolId
          },
          true
        );
      }

    } catch (error) {
      this.loggerService.error('Pusher: PM: wagerFacade: matrix')
    }
  }

  /**
   * TTL — pool total updates (matrix side panel)
   * @param {IPusher} data Pusher payload
   * @returns {void}
   */
  private TTL(data: IPusher): void {
    // let value: IPusherDataTTL | null;

    // try {
    //   value = JSON.parse(data?.Data);
    // }
    // catch(e) {
    //   value = null
    // }

    // this.loggerService.log('TTL:value', value)
  }


  /**
   * STI — single ticket issue; refreshes balance and active bets
   * @param {any} data Pusher payload
   * @param {object} accountFacade Account facade
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private STI(data: any, accountFacade?: object, wagerFacade?: object): void {
    this.loggerService.log('PusherService:STI:', {data})

    if (data?.Balance) {
      try {
        (accountFacade as any).setBalance(
          data?.Balance,
          'PusherService:STI',
        );
      } catch (error) {
        this.loggerService.error('Pusher: STI: accountFacade.setBalance')
      }
    }
    else {
      try {
        (accountFacade as any).getBalance(
          'PusherService:STI',
        );
      } catch (error) {
        this.loggerService.error('Pusher: STI: accountFacade.getBalance')
      }
    }


    try {
      const statuses = {
        3: 'active',
      };

      _.map([3], (status) => {
        (wagerFacade as any).getBetsAPI(
          {
            runId: this.runId,
            ticketStatus: status,
            offset: 0,
            next: 500
          },
          (statuses as any)[status],
          'PusherService:STI'
        );
      });
    } catch (error) {
      this.loggerService.error('Pusher: STI: wagerFacade.getBetsAPI')
    }
  }

  /**
   * TIC — ticket cancel; refreshes balance and active bets
   * @param {any} data Pusher payload
   * @param {object} accountFacade Account facade
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private TIC(data: any, accountFacade?: object, wagerFacade?: object): void {
    this.loggerService.log('PusherService:TIC:', {data})

    try {
      (accountFacade as any).getBalance(
        'PusherService:TIC',
      );
    } catch (error) {
      this.loggerService.error('Pusher: TIC: accountFacade.getBalance')
    }

    try {
      const statuses = {
        3: 'active',
      };

      _.map([3], (status) => {
        (wagerFacade as any).getBetsAPI(
          {
            runId: this.runId,
            ticketStatus: status,
            offset: 0,
            next: 500
          },
          (statuses as any)[status],
          'PusherService:TIC'
        );
      });
    } catch (error) {
      this.loggerService.error('Pusher: TIC: wagerFacade.getBetsAPI')
    }
  }

  /**
   * TP — ticket payoff; refreshes bets and balance when the user is owner
   * @param {IPusher} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @param {object} accountFacade Account facade
   * @param {any} myBets User's bets collection
   * @returns {void}
   */
  private TP(data: IPusher,  wagerFacade?: object, accountFacade?: object, myBets?: any): void {
    this.loggerService.log('PusherService:TP:data', data);

    let isExist = false;

    _.map([...myBets?.close||[], ...myBets?.active||[]], (ticket) => {
      if (ticket?.EventId === data?.EventId) isExist = true;
    });

    if (!isExist) return;

    try {
      const statuses = {
        6: 'close',
        3: 'active',
      };

      _.map([6, 3], (status) => {
        (wagerFacade as any).getBetsAPI(
          {
            runId: this.runId,
            ticketStatus: status,
            offset: 0,
            next: 500
          },
          (statuses as any)[status],
          'PusherService:TP'
        );
      });
    } catch (error) {
      this.loggerService.error('Pusher: TP: wagerFacade.getBetsAPI')
    }

    try {
      (accountFacade as any).getBalance(
        'PusherService:TP',
      );
    } catch (error) {
      this.loggerService.error('Pusher: TP: accountFacade.getBalance')
    }
  }

  /**
   * STC — stack ticket condition met
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private STC(data: any): void {

  }

  /**
   * SST — stack single ticket; refreshes saved bets
   * @param {any} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private SST(data: any, wagerFacade?: object): void {
    this.loggerService.log('PusherService:SST:data', data);

    try {
      const statuses = {
        1: 'saved',
      };

      _.map([1], (status) => {
        (wagerFacade as any).getBetsAPI(
          {
            runId: this.runId,
            ticketStatus: status,
            offset: 0,
            next: 500
          },
          (statuses as any)[status],
          'PusherService:SST'
        );
      });
    } catch (error) {
      this.loggerService.error('Pusher: SST: wagerFacade.getBetsAPI')
    }
  }

  /**
   * STS — stack ticket submit; refreshes balance and saved/active bets
   * @param {any} data Pusher payload
   * @param {object} accountFacade Account facade
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private STS(data: any, accountFacade?: object, wagerFacade?: object): void {
    this.loggerService.log('PusherService:STS:data', data);

    if (data?.Balance) {
      try {
        (accountFacade as any).setBalance(
          data?.Balance,
          'PusherService:STS',
        );
      } catch (error) {
        this.loggerService.error('Pusher: STS: accountFacade.setBalance')
      }
    }
    else {
      try {
        (accountFacade as any).getBalance(
          'PusherService:STS',
        );
      } catch (error) {
        this.loggerService.error('Pusher: STS: accountFacade.getBalance')
      }
    }


    try {
      const statuses = {
        1: 'saved',
        3: 'active',
      };

      _.map([1, 3], (status) => {
        (wagerFacade as any).getBetsAPI(
          {
            runId: this.runId,
            ticketStatus: status,
            offset: 0,
            next: 500
          },
          (statuses as any)[status],
          'PusherService:STS'
        );
      });
    } catch (error) {
      this.loggerService.error('Pusher: STS: wagerFacade.getBetsAPI')
    }
  }

  /**
   * STR — stack ticket remove; refreshes saved bets
   * @param {any} data Pusher payload
   * @param {object} wagerFacade Wager facade
   * @returns {void}
   */
  private STR(data: any, wagerFacade?: object): void {
    this.loggerService.log('PusherService:STR', {data});

    try {
      const statuses = {
        1: 'saved',
      };

      _.map([1], (status) => {
        (wagerFacade as any).getBetsAPI(
          {
            runId: this.runId,
            ticketStatus: status,
            offset: 0,
            next: 500
          },
          (statuses as any)[status],
          'PusherService:STR'
        );
      });
    } catch (error) {
      this.loggerService.error('Pusher: STR: wagerFacade.getBetsAPI')
    }
  }

  /**
   * FUS — file upload start
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private FUS(data: any): void {

  }

  /**
   * FUC — file upload complete
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private FUC(data: any): void {

  }

  /**
   * BAL — balance update; sets the user's balance
   * @param {IBalancePusher} data Pusher balance payload
   * @param {object} accountFacade Account facade
   * @returns {void}
   */
  private BAL(data: IBalancePusher, accountFacade?: object): void {
    this.loggerService.log('PusherService:BAL', {data});

    try {
      (accountFacade as any).setBalance(
        data?.Balance,
        'PusherService:BAL',
      );
    } catch (error) {
      this.loggerService.error('Pusher: BAL: accountFacade.setBalance')
    }

  }

  /**
   * ROL — role change; updates the role id cookie
   * @param {any} data Pusher payload
   * @param {object} sessionService Session service
   * @returns {void}
   */
  private ROL(data: any, sessionService?: object): void {
    this.loggerService.log('PusherService:ROL', {data});

    try {
      // (accountFacade as any).setRoleId(
      //   data?.RoleId,
      //   'PusherService:ROL',
      // );

      const options1day: Web.ICookie = (sessionService as any).getOptions('/', 1);
      (sessionService as any).setRoleId(data?.RoleId, options1day);

    } catch (error) {
      this.loggerService.error('Pusher: ROL: sessionService.setRoleId')
    }
  }

  /**
   * IDCHECK — identity check event
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private IDCHECK(data: any): void {

  }

  /**
   * DOCCHECK — document check event
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private DOCCHECK(data: any): void {

  }

  /**
   * LGN — login event; persists login response cookies
   * @param {any} data Pusher payload
   * @param {object} sessionService Session service
   * @returns {void}
   */
  private LGN(data: any, sessionService?: object): void {
    this.loggerService.log('PusherService:LGN', {data});

    try {
      (sessionService as any).storeCookiesLoginResponse(
        data,
        (sessionService as any).getUserEmail()
      );
    } catch (error) {
      this.loggerService.error('Pusher: LGN: sessionService.storeCookiesLoginResponse')
    }

    // if (data && data.Data) {
    //   let response: any;


      // try {
      //   response = JSON.parse(data?.Data);
      // }
      // catch(e) {
      //   response = null
      // }

      // this.setRoleId(response.RoleId);

      // TODO: ask Ralph on what data are thrown in the pusher
      // and ask on types of Data
      // and ask Ralph on the description on each properties
    // }
  }

  /**
   * EXC — exchanges update
   * @param {any} data Pusher payload
   * @returns {void}
   */
  private EXC(data: any): void {
  }


  /**
   * Store the selected event track and (re)subscribe to its odds channel
   * @param {IGetEventsDetailsResponse} selectedEventTrack Selected event track
   * @returns {void}
   */
  setSelectedEventTrack(selectedEventTrack?: IGetEventsDetailsResponse): void {
    if (!selectedEventTrack) return;

    // Always keep latest selection so connect() can (re)subscribe later
    this.runId = selectedEventTrack?.ToteResponse?.RunId;
    this.selectedEventTrack = selectedEventTrack;

    // Defer channel subscribe until Pusher is connected (avoids undefined.subscribe)
    if (!this.pusher) return;

    // TODO: put this code before selecting the new selected event
    if (this.updateOddsChannel) { // clean first
      try {
        this.pusher.unsubscribe('odds' + this.selectedEventTrack?.ToteResponse.EventDetail.EventId);
      }
      catch (error) {
        this.commonFacade.log(`Pusher unsubscribe odds: ${error}`);
      }

      this.updateOddsChannel = undefined;
    }

    // this.selectedEventTrack = selectedEventTrack; // moved above the pusher guard

    // odds
    this.updateOddsChannel = this.pusher.subscribe('odds' + this.selectedEventTrack?.ToteResponse.EventDetail.EventId);

    this.updateOddsChannel.unbind('ALL');
    this.updateOddsChannel.bind('ALL', (data: any) => {
      // this.loggerService.log('PusherService:updateOddsChannel:', data);
      this.processMessage(data);
    });
  }

  /**
   * Store the currently selected race
   * @param {any} selectedRace Selected race
   * @returns {void}
   */
  setSelectedRace(selectedRace?: any): void {
    if (!selectedRace) return;
    // this.loggerService.log('PusherService:selectedRace:', selectedRace);
    this.selectedRace = selectedRace;
  }

}
